import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { ChevronRight, ExternalLink, MapPin, Check, Share2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { subscribeToNewsletter } from "@/lib/brevo";

const HERO_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/NXjgILHSTXidEaJP.webp";
const OG_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/PNvXtvvgnmYpQrMs.jpg";

const ARTICLE1_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp";
const ARTICLE8_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/cbxBOxJCiaYOARrK.webp";
const ARTICLE9_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/SIuiZFHgwspEQwFc.webp";

const RELATED = [
  { tag: "Business Setup", title: "Company Setup Romania: Complete Guide for Expats", img: ARTICLE8_IMG, href: "/company-setup-romania" },
  { tag: "Relocation", title: "Why Move to Brașov? A Foreigner's Honest Guide to Romania's Best City", img: ARTICLE1_IMG, href: "/why-expats-choose-brasov" },
  { tag: "Real Estate", title: "How to Buy an Apartment in Brașov as a Foreigner", img: ARTICLE9_IMG, href: "/buying-apartment-foreigner-brasov" },
];

function NewsletterInlineLight() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await subscribeToNewsletter(email);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") return <p className="text-xs text-green-700 py-2">✓ Subscribed! See you Friday.</p>;

  return (
    <form onSubmit={handleSubmit}>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="w-full border border-stone-200 px-3 py-2 text-xs text-stone-700 outline-none focus:border-stone-400 mb-2"
      />
      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full py-2 text-xs text-white font-semibold disabled:opacity-60"
        style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
      >
        {status === "loading" ? "..." : "SUBSCRIBE"}
      </button>
      {status === "error" && <p className="text-xs text-red-500 mt-1">Try again.</p>}
    </form>
  );
}

function SidebarAd() {
  return (
    <div className="border border-stone-200 overflow-hidden">
      <div className="px-3 py-1.5" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner Content · Cartier Transilvania</span>
      </div>
      <div className="relative">
        <img
          src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp"
          alt="Cartier Transilvania Brașov"
          className="w-full h-48 object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, oklch(0.12 0.01 60 / 0.8) 0%, transparent 60%)" }} />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-xs uppercase tracking-widest mb-1 font-semibold">New development · Noua</p>
          <h4 className="text-white font-bold leading-tight" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
            Cartier Transilvania
          </h4>
        </div>
      </div>
      <div className="p-4">
        <p className="text-stone-600 text-sm font-light leading-relaxed mb-3">
          208 apartments in 6 blocks. T1–T3 completed &amp; occupied. T4–T6 delivering September 2027. Next to Lake Noua.
        </p>
        <div className="flex items-center gap-1 text-xs text-stone-500 mb-4">
          <MapPin size={11} />
          <span>Noua, Brașov</span>
        </div>
        <a
          href="https://www.cartiertransilvania.ro/en/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1 text-xs text-white w-full px-3 py-2 transition-opacity hover:opacity-90"
          style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
        >
          VIEW PROJECT <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}

function FeaturedDeveloper() {
  return (
    <div className="mt-16 p-8 border-l-4" style={{ backgroundColor: "oklch(0.985 0.006 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
      <div className="flex items-center gap-2 mb-5">
        <span className="text-xs text-stone-400 uppercase tracking-widest">Partner content · Cartier Transilvania</span>
      </div>
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <img
          src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp"
          alt="Cartier Transilvania"
          className="w-full md:w-56 h-40 object-cover flex-shrink-0"
          loading="lazy"
        />
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
            Apartments for sale · Noua, Brașov
          </p>
          <h3 className="font-bold text-stone-900 mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.3rem" }}>
            Cartier Transilvania — a development built to last
          </h3>
          <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.7" }}>
            208 apartments across 6 blocks in the Noua neighbourhood. T1, T2, and T3 are completed and occupied. T4, T5, and T6 are under construction with a delivery date of September 2027. Solar panels and heat pump heating system included. Next to Lake Noua, Brașov Zoo, and Parc Aventura.
          </p>
          <a
            href="https://www.cartiertransilvania.ro/en/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-white px-5 py-2.5 transition-opacity hover:opacity-90"
            style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
          >
            VISIT PROJECT SITE <ExternalLink size={11} />
          </a>
        </div>
      </div>
    </div>
  );
}

function ShareButton({ url, title }: { url: string; title: string }) {
  const [copied, setCopied] = useState(false);
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <button onClick={handleShare} className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-800 transition-colors">
      {copied ? <Check size={13} className="text-green-600" /> : <Share2 size={13} />}
      {copied ? "Copied!" : "Share"}
    </button>
  );
}

export default function ArticleWorkingRomania() {
  return (
    <>
      <Helmet>
        <title>Working in Romania as a Foreigner: The 2026 Law Explained</title>
        <meta name="description" content="Working in Romania as a foreigner just got a new legal framework. OUG 32/2026 introduces WorkinRomania.gov.ro, two visa types, and new rights for non-EU workers. Here is what it means for you." />
        <meta property="og:title" content="Working in Romania as a Foreigner: The 2026 Law Explained" />
        <meta property="og:description" content="Working in Romania as a foreigner just got a new legal framework. OUG 32/2026 introduces WorkinRomania.gov.ro, two visa types, and new rights for non-EU workers. Here is what it means for you." />
        <meta property="og:image" content={OG_IMG} />
        <meta property="og:image:width" content="1400" />
        <meta property="og:image:height" content="788" />
        <meta property="og:image:alt" content="Working in Romania as a foreigner — OUG 32/2026 guide for expats" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://brasovgateway.com/working-in-romania-foreigner-guide" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Working in Romania as a Foreigner: The 2026 Law Explained" />
        <meta name="twitter:description" content="Working in Romania as a foreigner just got a new legal framework. OUG 32/2026 introduces WorkinRomania.gov.ro, two visa types, and new rights for non-EU workers. Here is what it means for you." />
        <meta name="twitter:image" content={OG_IMG} />
        <meta name="keywords" content="working in Romania as a foreigner, work permit Romania, OUG 32 2026, WorkinRomania, visa D AM1 Romania, foreign worker Romania 2026" />
        <link rel="canonical" href="https://brasovgateway.com/working-in-romania-foreigner-guide" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Article",
                "@id": "https://brasovgateway.com/working-in-romania-foreigner-guide#article",
                "headline": "Working in Romania as a Foreigner: The 2026 Law Explained",
                "description": "Working in Romania as a foreigner just got a new legal framework. OUG 32/2026 introduces WorkinRomania.gov.ro, two visa types, and new rights for non-EU workers.",
                "datePublished": "2026-04-29",
                "dateModified": "2026-04-29",
                "author": {
                  "@type": "Organization",
                  "name": "Brașov Gateway",
                  "url": "https://brasovgateway.com"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Brașov Gateway",
                  "url": "https://brasovgateway.com"
                },
                "url": "https://brasovgateway.com/working-in-romania-foreigner-guide",
                "inLanguage": "en",
                "articleSection": "Work & Immigration"
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://brasovgateway.com" },
                  { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://brasovgateway.com/blog" },
                  { "@type": "ListItem", "position": 3, "name": "Working in Romania as a Foreigner", "item": "https://brasovgateway.com/working-in-romania-foreigner-guide" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <div className="min-h-screen" style={{ fontFamily: "Source Sans 3, sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>
        <SiteHeader />

        {/* Hero */}
        <div className="relative h-72 md:h-96 mt-16">
          <img
            src={HERO_IMG}
            alt="Working in Romania as a foreigner — OUG 32/2026 guide for expats"
            className="w-full h-full object-cover"
            fetchPriority="high"
            width="1400"
            height="788"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, oklch(0.985 0.006 75) 100%)" }} />
        </div>

        <div className="container py-8">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Main content */}
            <main className="flex-1 min-w-0">
              <article className="max-w-none">
                <header className="mb-10">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-xs font-bold uppercase tracking-widest px-3 py-1" style={{ backgroundColor: "oklch(0.38 0.12 15)", color: "white" }}>
                      Work &amp; Immigration
                    </span>
                    <span className="text-xs text-stone-500 uppercase tracking-widest">April 2026</span>
                  </div>
                  <h1 className="text-stone-900 leading-tight mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 800 }}>
                    Working in Romania as a foreigner: what the new 2026 law actually means for you
                  </h1>
                  <div className="flex items-center justify-between py-4 border-t border-b border-stone-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden" style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                        <img
                          src="https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/XbbLvxSueOHmjHcF.webp"
                          alt="Brașov Gateway"
                          className="w-full h-full object-cover opacity-50"
                        />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-stone-900">Brașov Gateway Editorial</p>
                        <p className="text-xs text-stone-500">Work &amp; Immigration Team</p>
                      </div>
                    </div>
                    <ShareButton url="https://brasovgateway.com/working-in-romania-foreigner-guide" title="Working in Romania as a Foreigner: The 2026 Law Explained" />
                  </div>
                </header>

                {/* Lead paragraph */}
                <p className="text-stone-600 text-lg md:text-xl font-light leading-relaxed mt-6 mb-8" style={{ lineHeight: "1.6" }}>
                  Working in Romania as a foreigner has always been possible — but the rules were scattered across multiple laws, the process was opaque, and the protections for workers were thin. On 27 April 2026, Romania published OUG 32/2026 in the Official Gazette, replacing the entire framework with a single, coherent system. This is the honest guide to what changed, what it means in practice, and what you need to do if you want to work here legally.
                </p>

                {/* Important notice */}
                <div className="my-8 p-5 border border-amber-200 bg-amber-50">
                  <p className="text-xs uppercase tracking-widest font-bold text-amber-700 mb-2">Important notice</p>
                  <p className="text-sm text-stone-700 font-light leading-relaxed">
                    OUG 32/2026 is an Emergency Ordinance — it entered into force on <strong>27 April 2026</strong>, the date of publication in the Official Gazette. As with all emergency ordinances in Romania, it must be approved, amended, or rejected by Parliament. The <a href="https://hub.mai.gov.ro/workinro/main/index-dezvoltare" target="_blank" rel="noopener noreferrer" className="underline decoration-dotted underline-offset-2" style={{ color: "oklch(0.6 0.1 30)" }}>WorkinRomania.gov.ro</a> platform becomes fully operational by <strong>7 August 2026</strong>, and the new procedures apply from that date. The provisions described in this guide reflect the text as published in the Official Gazette on 27 April 2026. If Parliament makes significant amendments, we will update this article accordingly.
                  </p>
                </div>

                {/* Legal disclaimer */}
                <div className="my-6 p-5 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>Legal disclaimer</p>
                  <p className="text-sm text-stone-600 font-light leading-relaxed">
                    This article is intended as a general guide only and does not constitute legal advice. While we have verified the information against the official text of OUG 32/2026 (Monitorul Oficial nr. 335/27.IV.2026), Romanian immigration and labour law is complex, and individual situations may differ. Sector quota figures are based on HG 1.169/2025 and are indicative. For decisions involving employment contracts, visa applications, or residence permits, we strongly recommend consulting a qualified Romanian lawyer or contacting IGI directly. If you have questions about your specific situation,{" "}
                    <a href="/contact" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>our team is here to help</a>.
                  </p>
                </div>

                {/* Quick Facts */}
                <div className="my-10 p-6 border-l-4" style={{ backgroundColor: "oklch(0.97 0.005 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
                  <h3 className="text-xs uppercase tracking-widest font-bold mb-5" style={{ color: "oklch(0.38 0.12 15)" }}>
                    Quick facts: OUG 32/2026 at a glance
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { label: "Published", value: "27 April 2026", note: "Monitorul Oficial Nr. 335" },
                      { label: "In force from", value: "27 April 2026", note: "Date of publication; platform operational by 7 Aug 2026" },
                      { label: "Platform", value: "WorkinRomania.gov.ro", note: "Fully operational by 7 August 2026" },
                      { label: "Annual quota (2026)", value: "100,000 non-EU workers", note: "Set by Government Decision; split by sector" },
                      { label: "Visa types introduced", value: "D/AM1 and D/AM2", note: "Direct employer or via placement agency" },
                      { label: "Replaces", value: "OUG 25/2014 (Title I)", note: "The previous framework on hiring foreigners, now abrogated" },
                    ].map((fact, i) => (
                      <div key={i} className="p-4 border border-stone-100" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
                        <p className="text-xs uppercase tracking-widest text-stone-400 mb-1">{fact.label}</p>
                        <p className="font-bold text-stone-900 text-sm leading-tight mb-1" style={{ fontFamily: "Playfair Display, serif" }}>{fact.value}</p>
                        <p className="text-xs text-stone-500 font-light">{fact.note}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Table of Contents */}
                <div className="my-8 p-6 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: "oklch(0.38 0.12 15)" }}>In this guide</p>
                  <ol className="space-y-2">
                    {[
                      { n: "01", label: "Who is affected — and who is not", anchor: "#who-is-affected" },
                      { n: "02", label: "The two routes to legal employment", anchor: "#two-routes" },
                      { n: "03", label: "What must be in your job offer — and what to demand", anchor: "#job-offer" },
                      { n: "04", label: "The visa process: D/AM1 and D/AM2 step by step", anchor: "#visa-process" },
                      { n: "05", label: "Special categories: Blue Card, ICT, seasonal, au pair", anchor: "#special-categories" },
                      { n: "06", label: "Your rights as a foreign worker", anchor: "#your-rights" },
                      { n: "07", label: "Can you change employer?", anchor: "#change-employer" },
                      { n: "08", label: "Red flags and what is illegal", anchor: "#red-flags" },
                      { n: "09", label: "The sectors with the most available positions", anchor: "#sectors" },
                      { n: "10", label: "The full timeline: from job offer to first payslip", anchor: "#timeline" },
                    ].map((item) => (
                      <li key={item.n} className="flex items-start gap-3">
                        <span className="text-xs font-bold flex-shrink-0 mt-0.5" style={{ color: "oklch(0.38 0.12 15)" }}>{item.n}</span>
                        <a href={item.anchor} className="text-stone-600 text-sm font-light hover:text-stone-900 transition-colors no-underline" style={{ lineHeight: "1.5" }}>
                          {item.label}
                        </a>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Section 1 */}
                <h2 id="who-is-affected" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Who is affected — and who is not
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  OUG 32/2026 applies exclusively to <strong>non-EU, non-EEA, and non-Swiss nationals</strong> who want to work in Romania. If you hold a passport from an EU member state, Norway, Iceland, Liechtenstein, or Switzerland, this law does not apply to you — you retain full freedom of movement and the right to work in Romania without any permit or registration on WorkinRomania.gov.ro.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  For everyone else — citizens of the US, UK, Canada, India, Turkey, the Philippines, Nepal, Sri Lanka, and all other non-EU countries — this is the new framework. It covers all forms of employment: permanent contracts, seasonal work, intra-company transfers, internships, au pair arrangements, and highly qualified positions under the EU Blue Card scheme.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  One category worth noting separately: <strong>self-employment and freelancing</strong> are not covered by this ordinance. If you want to work in Romania as a freelancer or run your own business, the relevant path is{" "}
                  <Link href="/company-setup-romania" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                    setting up a Romanian SRL
                  </Link>
                  , which has its own separate procedure and timeline.
                </p>

                {/* Section 2 */}
                <h2 id="two-routes" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  The two routes to legal employment
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The law creates two distinct paths, depending on whether your employer is registered directly on the platform or works through an authorised placement agency. Understanding the difference matters because it affects the visa type you receive, who submits the paperwork, and what protections apply.
                </p>

                {/* Routes comparison table */}
                <div className="my-6 w-full overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"] }}>
                  <table className="border-collapse" style={{ width: "100%", fontSize: "clamp(0.65rem, 2.2vw, 0.875rem)" }}>
                    <thead>
                      <tr style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                        {["", "Route 1 — D/AM1", "Route 2 — D/AM2"].map((h) => (
                          <th key={h} className="text-left text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "0.6rem 0.8rem", fontSize: "0.7rem", letterSpacing: "0.05em" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: "Employer type", am1: "Registered employer on WorkinRomania.gov.ro", am2: "Authorised placement agency" },
                        { label: "Who submits the application", am1: "The employer", am2: "The agency (or authorised employer)" },
                        { label: "Contract required", am1: "Individual employment contract (REGES-ONLINE)", am2: "Tripartite placement contract (agency + employer + worker)" },
                        { label: "Employer requirements", am1: "Min. 1 year of activity, no tax arrears, no criminal record", am2: "Same + agency must hold ANOFM authorisation" },
                        { label: "Authorised employer (no agency)", am1: "N/A", am2: "Min. 24 months activity, min. 50 employees, €1,000/worker financial guarantee" },
                        { label: "Processing time", am1: "30 calendar days", am2: "30 calendar days" },
                        { label: "Best for", am1: "Direct hire by a Romanian company", am2: "Recruitment through a staffing agency" },
                      ].map((row, i) => (
                        <tr key={i} className="border-b border-stone-100" style={{ backgroundColor: i % 2 === 0 ? "oklch(0.985 0.006 75)" : "white" }}>
                          <td className="text-stone-700 font-semibold" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.label}</td>
                          <td className="text-stone-600 font-light" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.am1}</td>
                          <td className="text-stone-600 font-light" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.am2}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  In practice, most foreign workers arriving through direct recruitment will follow Route 1. Route 2 is more common in sectors like construction and agriculture, where staffing agencies play a large role in matching workers with employers. Both routes lead to the same outcome — a work authorisation and a long-stay visa — but the paperwork trail is different.
                </p>

                {/* Section 3 */}
                <h2 id="job-offer" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  What must be in your job offer — and what to demand
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Before any visa application can be submitted, the employer must issue a <strong>firm offer (ofertă fermă)</strong>. This is not a letter of intent or a verbal agreement — it is a legally defined document with mandatory content. If anything on the list below is missing, the application will be rejected by IGI, and you will have wasted weeks of processing time. Read this list carefully before you sign anything.
                </p>

                <div className="my-6 p-6 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-bold mb-4" style={{ color: "oklch(0.38 0.12 15)" }}>Mandatory content of the firm offer (Art. 6, OUG 32/2026)</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      "Duration of the offer and number of available positions",
                      "Job title, occupation, and specific requirements",
                      "Duration of employment (months/days) and conditions",
                      "Maximum working hours and rest periods",
                      "Gross and net remuneration",
                      "Guaranteed minimum wage reference",
                      "Bonuses, supplements, and allowances",
                      "Annual leave entitlement",
                      "Health and safety conditions",
                      "Applicable taxes, contributions, and deductions",
                      "Accommodation (if provided): conditions and cost — cannot exceed 25% of net salary",
                      "Transport (if provided): conditions and whether it is paid by the employer",
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <Check size={13} className="flex-shrink-0 mt-0.5" style={{ color: "oklch(0.38 0.12 15)" }} />
                        <span className="text-sm text-stone-600 font-light">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The offer must be provided in Romanian and in a language the worker understands. If your employer hands you a document only in Romanian and you do not speak the language, ask for a translation before signing — this is your legal right under Art. 24 of the ordinance. The contract itself (individual employment contract) must also be bilingual.
                </p>

                {/* Section 4 */}
                <h2 id="visa-process" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  The visa process: D/AM1 and D/AM2 step by step
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The application is submitted by the employer (or agency), not by the worker. This is the <strong>single application procedure</strong> — one submission that covers both the work authorisation and the long-stay visa. You do not need to file separately for each. The processing authority is IGI (Inspectoratul General pentru Imigrări — the General Inspectorate for Immigration).
                </p>

                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The documents required from the worker's side are the same for both D/AM1 and D/AM2:
                </p>

                <div className="my-6 p-6 border border-stone-200" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                  <p className="text-xs uppercase tracking-widest font-bold mb-4" style={{ color: "oklch(0.38 0.12 15)" }}>Documents required from the worker</p>
                  <div className="space-y-2">
                    {[
                      { doc: "Valid passport", note: "Must remain valid for the entire duration of the visa" },
                      { doc: "Travel medical insurance", note: "Minimum €30,000 coverage, valid across all EU member states" },
                      { doc: "Criminal record certificate", note: "From your country of origin, translated and legalised" },
                      { doc: "Proof of financial means", note: "Equivalent to the gross minimum wage in Romania for 90 days" },
                      { doc: "Registered employment contract", note: "Registered in REGES-ONLINE by the employer before submission" },
                      { doc: "Tripartite placement contract (D/AM2 only)", note: "Signed by agency, employer, and worker" },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3 py-2 border-b border-stone-100 last:border-0">
                        <ChevronRight size={13} className="flex-shrink-0 mt-1" style={{ color: "oklch(0.38 0.12 15)" }} />
                        <div>
                          <span className="text-sm font-semibold text-stone-800">{item.doc}</span>
                          <span className="text-sm text-stone-500 font-light"> — {item.note}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The standard processing time is <strong>30 calendar days</strong> from the date of submission. Once approved, the work authorisation allows the worker to obtain a long-stay visa (D/AM1 or D/AM2) at the Romanian consulate or embassy in their country of origin. The visa is valid for the duration of the employment contract, up to one year, and is renewable.
                </p>

                {/* Section 5 */}
                <h2 id="special-categories" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Special categories: Blue Card, ICT, seasonal, au pair
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  OUG 32/2026 introduces seven distinct work authorisation types. The standard permanent worker category covers most cases, but the special categories have specific requirements and benefits worth knowing.
                </p>

                {/* Special categories table */}
                <div className="my-6 w-full overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"] }}>
                  <table className="border-collapse" style={{ width: "100%", fontSize: "clamp(0.65rem, 2.2vw, 0.875rem)" }}>
                    <thead>
                      <tr style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                        {["Category", "Key requirement", "Duration", "Notable benefit"].map((h) => (
                          <th key={h} className="text-left text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "0.6rem 0.8rem", fontSize: "0.7rem", letterSpacing: "0.05em" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { cat: "EU Blue Card (highly qualified)", req: "Contract min. 6 months; salary ≥ 1.5× average gross wage; higher education or 5 years relevant experience", dur: "1 year, renewable", benefit: "Faster path to long-term EU residence" },
                        { cat: "ICT (intra-company transfer)", req: "Min. 3–12 months with the sending company; specialist, manager, or trainee role", dur: "Max. 3 years (managers/specialists), 1 year (trainees)", benefit: "No need to apply through WorkinRomania" },
                        { cat: "Seasonal worker", req: "Contract in agriculture, tourism, or HoReCa; employer must provide free accommodation", dur: "Max. 180 days in any 365-day period", benefit: "Simplified procedure; employer bears accommodation cost" },
                        { cat: "Au pair", req: "Age 18–30; host family in Romania; max. 25 hours/week", dur: "Up to 1 year", benefit: "Minimum wage guaranteed; language and cultural exchange" },
                        { cat: "Intern/trainee", req: "Enrolled in or recently graduated from higher education; training agreement", dur: "Up to 6 months", benefit: "Lower administrative burden for employer" },
                        { cat: "Detached worker", req: "Valid residence permit in EU country of origin; detachment contract translated and legalised", dur: "Duration of detachment", benefit: "Social security stays in country of origin" },
                      ].map((row, i) => (
                        <tr key={i} className="border-b border-stone-100" style={{ backgroundColor: i % 2 === 0 ? "oklch(0.985 0.006 75)" : "white" }}>
                          <td className="text-stone-800 font-semibold" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.cat}</td>
                          <td className="text-stone-500 font-light" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.req}</td>
                          <td className="text-stone-700" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.dur}</td>
                          <td className="text-stone-600 font-light" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.benefit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The EU Blue Card is the most relevant category for professionals in IT, healthcare, engineering, and finance. If your salary qualifies — and in Romania's tech sector it often does — this route offers a faster path to long-term EU residence than the standard work permit, and it is recognised across all EU member states.
                </p>

                {/* Section 6 */}
                <h2 id="your-rights" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Your rights as a foreign worker
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  One of the most significant changes in OUG 32/2026 is the explicit codification of worker rights. The previous framework (OUG 56/2007) was largely focused on employer obligations and administrative procedures. The new law dedicates an entire title to the rights of foreign workers — and these rights are enforceable.
                </p>

                <div className="my-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { title: "Equal treatment", body: "Same salary, same minimum wage, same leave entitlement, same social security rights as Romanian workers. No two-tier system." },
                    { title: "Right to information", body: "Before signing any contract, you must receive full information about your rights, obligations, and working conditions — in a language you understand." },
                    { title: "Decent accommodation", body: "If the employer provides housing, the cost cannot exceed 25% of your net salary. The accommodation cannot be made conditional on continuing the employment contract." },
                    { title: "Portable social rights", body: "Pension and health contributions are transferable under bilateral agreements. Your years of contribution in Romania count." },
                    { title: "Access to complaints procedures", body: "Your employer is legally required to give you access to grievance and abuse reporting mechanisms. This includes the right to contact ITM (labour inspectorate) directly." },
                    { title: "Romanian language training", body: "Your employer must provide Romanian language courses for a minimum of 6 months, at least 6 hours per week. This is a legal obligation, not a perk." },
                  ].map((item, i) => (
                    <div key={i} className="p-5 border border-stone-200" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
                      <p className="text-sm font-bold text-stone-900 mb-2">{item.title}</p>
                      <p className="text-sm text-stone-600 font-light leading-relaxed">{item.body}</p>
                    </div>
                  ))}
                </div>

                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The law also imposes a specific obligation on employers: salary must be paid <strong>into a bank account</strong>. Cash payments are not permitted for foreign workers. This is both a protection for the worker and a transparency mechanism for tax authorities.
                </p>

                {/* Section 7 */}
                <h2 id="change-employer" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Can you change employer?
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  This is one of the most important practical questions for any foreign worker, and the answer is nuanced. Under OUG 32/2026, the rules differ depending on which route you came through.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  If you arrived through <strong>Route 2 (D/AM2 — via a placement agency)</strong>, you cannot change employer on your own initiative for the first <strong>6 months</strong> after your employment is registered in REGES-ONLINE. After 6 months, you can change employer, but only through the placement agency that is party to your placement contract — and this restriction applies until you have completed <strong>2 years</strong> of registered employment in Romania. After 2 years, you have more flexibility.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The exception to the 6-month restriction applies if your employer <strong>seriously fails to meet the contractual conditions</strong> — for example, not paying your salary, not providing the agreed accommodation, or violating health and safety obligations. In that case, the placement agency must identify at least two alternative job offers within 90 days. If no alternative is found within that window, the original employer is legally required to cover your return travel costs.
                </p>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  If you arrived through <strong>Route 1 (D/AM1 — direct hire)</strong>, the employer-change rules are governed by the general provisions of Romanian labour law and your residence permit conditions, rather than by the placement contract framework. These restrictions are a deliberate policy choice — designed to give employers confidence that the workers they sponsor will stay. It is worth factoring this into your decision before accepting a position.
                </p>

                {/* Section 8 */}
                <h2 id="red-flags" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  Red flags and what is illegal
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  OUG 32/2026 is explicit about what placement agencies and employers are <strong>not allowed to do</strong>. If you encounter any of the following, it is not just a warning sign — it is a legal violation that can be reported to ANOFM or ITM.
                </p>

                <div className="my-6 p-6 border-l-4 border-red-300" style={{ backgroundColor: "oklch(0.98 0.005 20)" }}>
                  <p className="text-xs uppercase tracking-widest font-bold text-red-700 mb-4">Illegal practices — report to ANOFM or ITM</p>
                  <div className="space-y-3">
                    {[
                      { flag: "Agency charges you a fee", detail: "Placement agencies are legally prohibited from charging fees to workers. If anyone asks you to pay for job placement, recruitment, or visa assistance, it is illegal. Fines for agencies: 20,000–50,000 lei." },
                      { flag: "Employer or agency retains your documents", detail: "No one is allowed to hold your passport, identity card, or residence permit. This is explicitly prohibited under Art. 22 of the ordinance. Fines: 10,000–20,000 lei." },
                      { flag: "No written contract in your language", detail: "Your employment contract must be in Romanian and in a language you understand. A verbal agreement or a contract only in Romanian is not legally sufficient." },
                      { flag: "Salary paid in cash", detail: "All salaries for foreign workers must be paid into a bank account. Cash payment is prohibited. Fines for employers: 3,000–5,000 lei." },
                      { flag: "Accommodation conditional on employment", detail: "If your employer provides housing, they cannot make it conditional on you staying in the job. You cannot be evicted immediately upon contract termination." },
                      { flag: "Agency operating without ANOFM authorisation", detail: "Check the public register R.A.P.S. (Registrul Agențiilor de Plasare a Străinilor) before signing anything with an agency. Fines for unauthorised activity: 25,000–40,000 lei per worker placed." },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3 py-3 border-b border-red-100 last:border-0">
                        <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0 mt-2" />
                        <div>
                          <p className="text-sm font-bold text-stone-800 mb-1">{item.flag}</p>
                          <p className="text-sm text-stone-600 font-light leading-relaxed">{item.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Section 9 */}
                <h2 id="sectors" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  The sectors with the most available positions
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The 2026 annual quota of 100,000 non-EU workers is distributed across sectors by Government Decision (HG 1.169/2025). The breakdown below reflects Romania’s most acute labour shortages — and it tells you where your chances of finding a sponsored position are highest. Note that OUG 32/2026 itself does not specify sector quotas; those are set separately by the Government.
                </p>

                {/* Sectors table */}
                <div className="my-6 w-full overflow-x-auto" style={{ WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"] }}>
                  <table className="border-collapse" style={{ width: "100%", fontSize: "clamp(0.65rem, 2.2vw, 0.875rem)" }}>
                    <thead>
                      <tr style={{ backgroundColor: "oklch(0.95 0.008 75)" }}>
                        {["Sector", "2026 quota", "Example occupations"].map((h) => (
                          <th key={h} className="text-left text-stone-600 font-semibold uppercase tracking-wider border-b border-stone-200" style={{ padding: "0.6rem 0.8rem", fontSize: "0.7rem", letterSpacing: "0.05em" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { sector: "Construction", quota: "35,000", roles: "Mason, carpenter, plumber, electrician, welder" },
                        { sector: "Industry & manufacturing", quota: "25,000", roles: "Production operator, welder, lathe operator, quality inspector" },
                        { sector: "Agriculture", quota: "20,000", roles: "Agricultural worker, machinery operator, greenhouse worker" },
                        { sector: "Services (HoReCa, retail, logistics)", quota: "20,000", roles: "Chef, waiter, housekeeper, warehouse operator, driver" },
                        { sector: "IT & technology", quota: "Included in services", roles: "Software developer, systems analyst, network administrator" },
                        { sector: "Healthcare", quota: "Included in services", roles: "Nurse, care worker, medical assistant" },
                      ].map((row, i) => (
                        <tr key={i} className="border-b border-stone-100" style={{ backgroundColor: i % 2 === 0 ? "oklch(0.985 0.006 75)" : "white" }}>
                          <td className="text-stone-800 font-semibold" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.sector}</td>
                          <td className="text-stone-700 font-bold" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.quota}</td>
                          <td className="text-stone-500 font-light" style={{ padding: "0.55rem 0.8rem", lineHeight: 1.4 }}>{row.roles}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-xs text-stone-400 font-light mb-4">* Sector breakdown based on HG 1.169/2025. Figures are indicative; the exact distribution is subject to revision by Government Decision.</p>

                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  The list of deficit occupations is updated semi-annually by the Ministry of Labour and published in the Official Gazette via ministerial order. It is also accessible through{" "}
                  <a
                    href="https://hub.mai.gov.ro/workinro/main/index-dezvoltare"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors"
                    style={{ color: "oklch(0.38 0.12 15)" }}
                  >
                    WorkinRomania.gov.ro
                  </a>{" "}
                  once the platform is fully operational. Authorised employers can hire directly from this list without going through a placement agency — which simplifies the process considerably for both sides.
                </p>

                {/* Section 10 */}
                <h2 id="timeline" className="text-stone-900 mt-12 mb-4" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700 }}>
                  The full timeline: from job offer to first payslip
                </h2>
                <p className="text-stone-600 font-light leading-relaxed mb-4" style={{ lineHeight: "1.8" }}>
                  Understanding the sequence of steps — and who is responsible for each — is essential. Delays almost always happen because one step was started before the previous one was completed. Here is the correct order.
                </p>

                <div className="my-6 space-y-0">
                  {[
                    { step: "01", title: "Firm offer issued by employer", detail: "Employer publishes the firm offer on WorkinRomania.gov.ro. You review it, confirm all mandatory fields are present, and accept.", who: "Employer" },
                    { step: "02", title: "Employment contract registered in REGES-ONLINE", detail: "Before any application is submitted to IGI, the individual employment contract must be registered in the national employment register (REGES-ONLINE) by the employer.", who: "Employer" },
                    { step: "03", title: "Single application submitted to IGI", detail: "Employer (or agency) submits the complete application package through WorkinRomania.gov.ro. Processing time: 30 calendar days.", who: "Employer / Agency" },
                    { step: "04", title: "Work authorisation issued", detail: "IGI issues the work authorisation. This document is the basis for the visa application.", who: "IGI" },
                    { step: "05", title: "Long-stay visa (D/AM1 or D/AM2) obtained", detail: "You apply at the Romanian consulate or embassy in your country of origin, presenting the work authorisation and the required documents.", who: "Worker" },
                    { step: "06", title: "Arrival in Romania", detail: "You enter Romania on the D/AM1 or D/AM2 visa. You have 90 days to register your residence.", who: "Worker" },
                    { step: "07", title: "Residence permit application", detail: "Within 90 days of arrival, you apply for a residence permit (permis de ședere) at the local IGI office. This is separate from the visa.", who: "Worker" },
                    { step: "08", title: "First payslip", detail: "Salary is paid monthly into your Romanian bank account. Your employer is required to provide a payslip showing gross salary, deductions, and net amount.", who: "Employer" },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 pb-6 relative">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                          {item.step}
                        </div>
                        {i < 7 && <div className="w-px flex-1 mt-2" style={{ backgroundColor: "oklch(0.85 0.01 75)" }} />}
                      </div>
                      <div className="pb-2">
                        <p className="font-bold text-stone-900 text-sm mb-1">{item.title}</p>
                        <p className="text-sm text-stone-600 font-light leading-relaxed mb-1">{item.detail}</p>
                        <span className="text-xs uppercase tracking-widest px-2 py-0.5" style={{ backgroundColor: "oklch(0.95 0.008 75)", color: "oklch(0.38 0.12 15)" }}>
                          {item.who}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* CTA box */}
                <div className="my-12 p-8 border-l-4" style={{ backgroundColor: "oklch(0.97 0.005 75)", borderLeftColor: "oklch(0.38 0.12 15)" }}>
                  <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: "oklch(0.38 0.12 15)" }}>Planning to work or build a business in Romania?</p>
                  <p className="text-stone-700 font-light leading-relaxed mb-5">
                    If you are considering relocating to Romania — whether as an employee or as someone who wants to{" "}
                    <Link href="/company-setup-romania" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                      set up your own company
                    </Link>{" "}
                    — Brașov is consistently the city that expats choose. The{" "}
                    <Link href="/cost-of-living-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                      cost of living
                    </Link>{" "}
                    is significantly lower than Bucharest, the quality of life is high, and the international community is growing. If you want to understand what settling here actually looks like, start with our{" "}
                    <Link href="/why-expats-choose-brasov" className="underline decoration-dotted underline-offset-2 hover:text-stone-900 transition-colors" style={{ color: "oklch(0.38 0.12 15)" }}>
                      honest guide for expats
                    </Link>
                    .
                  </p>
                  <a
                    href="/contact"
                    className="inline-flex items-center gap-2 text-xs text-white px-5 py-2.5 transition-opacity hover:opacity-90"
                    style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}
                  >
                    SPEAK WITH OUR TEAM <ChevronRight size={12} />
                  </a>
                </div>

                <FeaturedDeveloper />

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-10 mb-6">
                  {["Work Permit Romania", "OUG 32/2026", "Foreign Workers", "Immigration Romania", "WorkinRomania", "EU Blue Card"].map((tag) => (
                    <span key={tag} className="text-xs px-3 py-1 border border-stone-200 text-stone-500 uppercase tracking-widest" style={{ backgroundColor: "oklch(0.97 0.005 75)" }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </article>

              {/* Related articles */}
              <section className="mt-16 pt-10 border-t border-stone-200">
                <p className="text-xs uppercase tracking-widest font-semibold mb-8" style={{ color: "oklch(0.38 0.12 15)" }}>— More from the blog</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {RELATED.map((item) => (
                    <Link key={item.href} href={item.href} className="group block no-underline">
                      <div className="overflow-hidden mb-3">
                        <img
                          src={item.img}
                          alt={item.title}
                          className="w-full h-44 object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <p className="text-xs uppercase tracking-widest font-bold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>{item.tag}</p>
                      <h3 className="text-stone-900 font-bold leading-snug mb-3 group-hover:text-stone-600 transition-colors" style={{ fontFamily: "Playfair Display, serif", fontSize: "1rem" }}>
                        {item.title}
                      </h3>
                      <span className="text-xs uppercase tracking-widest font-semibold flex items-center gap-1" style={{ color: "oklch(0.38 0.12 15)" }}>
                        Read more <ChevronRight size={11} />
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            </main>

            {/* Sidebar */}
            <aside className="w-full lg:w-72 flex-shrink-0">
              <div className="sticky top-24 flex flex-col gap-6">
                <SidebarAd />
                <div className="p-5 border border-stone-200 bg-white">
                  <h4 className="font-bold text-stone-900 mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                    About Brașov Gateway
                  </h4>
                  <p className="text-stone-500 text-sm font-light leading-relaxed mb-4">
                    We help foreigners move to, invest in, and build a life in Brașov. Real estate, company setup, relocation support — all in one place.
                  </p>
                  <a href="/#contact"
                    className="block text-center text-xs text-white py-2.5 transition-opacity hover:opacity-90 no-underline"
                    style={{ backgroundColor: "oklch(0.38 0.12 15)", letterSpacing: "0.06em" }}>
                    GET IN TOUCH
                  </a>
                </div>
                <div className="mt-6 p-5" style={{ backgroundColor: "oklch(0.18 0.01 60)" }}>
                  <h4 className="font-bold text-white mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                    Stay in the loop
                  </h4>
                  <p className="text-stone-300 text-xs font-light leading-relaxed mb-4">
                    No spam. One email per week with what is actually happening in Brașov.
                  </p>
                  <NewsletterInlineLight />
                </div>
                {/* CTA card — Company Setup */}
                <div className="p-5 text-white" style={{ backgroundColor: "oklch(0.38 0.12 15)" }}>
                  <p className="text-xs uppercase tracking-widest mb-3 opacity-70">Business services</p>
                  <h4 className="font-bold mb-3 leading-snug" style={{ fontFamily: "Playfair Display, serif", fontSize: "1.1rem" }}>
                    Opening a company in Romania?
                  </h4>
                  <p className="text-sm font-light leading-relaxed mb-4 opacity-80">
                    We handle the full SRL registration process — from name reservation to ANAF registration.
                  </p>
                  <Link href="/services/company-setup">
                    <button className="w-full text-xs py-2.5 border border-white/40 hover:bg-white/10 transition-colors tracking-widest">
                      VIEW SERVICE
                    </button>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>

        <SiteFooter />
      </div>
    </>
  );
}
